// KAN-156: real-time POSITION_FILLED events for school admins.
//
// Primary: Server-Sent Events (EventSource) -> ~1-2s latency.
// Fallback: if the stream can't open (proxy/network blocks it) or keeps
// failing, poll /api/events/position-filled every 5s. While polling we retry
// the live stream every few minutes.
//
// Guarantees: only events created after the listener starts are delivered
// (server baselines at connect time), and each event_id is delivered once.
import { useEffect, useRef } from 'react';
import { EVENTS_STREAM_URL, events as eventsApi } from '@/lib/api';

export const POLL_INTERVAL_MS = 5000;
const SSE_OPEN_TIMEOUT_MS = 6000;
const SSE_MAX_FAILURES = 3;
const SSE_RETRY_FROM_POLL_MS = 3 * 60 * 1000;
const SEEN_CAP = 200;

const VALID_FROM = ['open', 'offered'];
const VALID_TO = ['filled', 'accepted'];

/** Only genuine live OPEN/OFFERED -> FILLED/ACCEPTED transitions qualify. */
export const isFillTransition = (evt) => !!evt
  && evt.type === 'POSITION_FILLED'
  && VALID_FROM.includes(String(evt.previous_status || '').toLowerCase())
  && VALID_TO.includes(String(evt.status || '').toLowerCase());

/**
 * @param {{ enabled: boolean, token: string|null, onEvent: (evt) => void, onModeChange?: (mode) => void }} opts
 */
export function usePositionFilledEvents({ enabled, token, onEvent, onModeChange }) {
  const onEventRef = useRef(onEvent);
  const onModeRef = useRef(onModeChange);
  onEventRef.current = onEvent;
  onModeRef.current = onModeChange;

  useEffect(() => {
    if (!enabled || !token) return undefined;

    let disposed = false;
    let es = null;
    let openTimer = null;
    let pollTimer = null;
    let sseRetryTimer = null;
    let sseFailures = 0;
    let sseReady = false;
    let cursor = null; // last server_time we know we're caught up to
    const seen = new Set();

    const setMode = (mode) => onModeRef.current?.(mode);

    const deliver = (evt) => {
      if (!evt || !evt.event_id || seen.has(evt.event_id)) return;
      seen.add(evt.event_id);
      if (seen.size > SEEN_CAP) seen.delete(seen.values().next().value);
      if (!isFillTransition(evt)) return;
      try { onEventRef.current?.(evt); } catch (_e) { /* listener errors must not kill the stream */ }
    };

    const closeSse = () => {
      if (openTimer) { clearTimeout(openTimer); openTimer = null; }
      if (es) { es.close(); es = null; }
    };

    const stopPolling = () => {
      if (pollTimer) { clearInterval(pollTimer); pollTimer = null; }
      if (sseRetryTimer) { clearTimeout(sseRetryTimer); sseRetryTimer = null; }
    };

    const pollOnce = async () => {
      try {
        const res = await eventsApi.pollPositionFilled(cursor);
        if (disposed) return;
        (res.data?.events || []).forEach(deliver);
        if (res.data?.server_time) cursor = res.data.server_time;
      } catch (err) {
        if (err?.response?.status === 401 || err?.response?.status === 403) stopPolling();
      }
    };

    const startPolling = async () => {
      if (disposed || pollTimer) return;
      closeSse();
      setMode('polling');
      if (!cursor) await pollOnce(); // baseline only: returns no historical events
      if (disposed) return;
      pollTimer = setInterval(pollOnce, POLL_INTERVAL_MS);
      sseRetryTimer = setTimeout(() => {
        if (disposed) return;
        stopPolling();
        startSse();
      }, SSE_RETRY_FROM_POLL_MS);
    };

    const startSse = () => {
      if (disposed) return;
      if (typeof window.EventSource === 'undefined') { startPolling(); return; }
      sseReady = false;
      const params = new URLSearchParams({ token });
      if (cursor) params.set('since', cursor);
      try {
        es = new window.EventSource(`${EVENTS_STREAM_URL}?${params.toString()}`);
      } catch (_e) {
        startPolling();
        return;
      }
      openTimer = setTimeout(() => { if (!sseReady) startPolling(); }, SSE_OPEN_TIMEOUT_MS);

      const markAlive = (e) => {
        try {
          const data = JSON.parse(e.data || '{}');
          if (data.server_time) cursor = data.server_time;
        } catch (_err) { /* ignore */ }
      };
      es.addEventListener('ready', (e) => {
        sseReady = true;
        sseFailures = 0;
        if (openTimer) { clearTimeout(openTimer); openTimer = null; }
        markAlive(e);
        setMode('live');
      });
      es.addEventListener('ping', markAlive);
      es.addEventListener('position_filled', (e) => {
        try { deliver(JSON.parse(e.data)); } catch (_err) { /* malformed */ }
      });
      es.onerror = () => {
        // EventSource auto-reconnects (resuming via Last-Event-ID). Fall back to
        // polling if it never opened, or keeps failing.
        sseFailures += 1;
        if (!sseReady || sseFailures >= SSE_MAX_FAILURES) startPolling();
        sseReady = false;
      };
    };

    startSse();

    return () => {
      disposed = true;
      closeSse();
      stopPolling();
    };
  }, [enabled, token]);
}
